import React, { use } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDownIcon, PlusCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useDublicatePricingCampaignsMutation } from '@/entities/pricing-campaign'
import { useScenariosQuery } from '@/entities/scenario'

import { Button } from '@/shared/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/shared/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { useDisclosure } from '@/shared/hooks/use-disclosure'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { processingMessage, successActionMessage } from '@/shared/tools/message'

import { PricingCampaignsContext } from '../providers/PricingCampaignsProvider'

export default function AssignToScenarioDropdown({
	optimizationGroupId,
}: {
	optimizationGroupId: string
}) {
	const { t } = useTranslation()

	const { selected, setSelected } = use(PricingCampaignsContext)
	const { showModal } = useModals()
	const queryClient = useQueryClient()
	const popover = useDisclosure()
	const dublicate = useDublicatePricingCampaignsMutation({
		onMutate() {
			toast.loading(processingMessage(t), {
				id: 'scenario-copy-pcs',
			})
			popover.close()
			setSelected([])
		},
		onSuccess() {
			toast.success(successActionMessage('Pricing campaign(s)', 'copied', t), {
				duration: 3000,
				id: 'scenario-copy-pcs',
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
		},
	})
	const scenariosQuery = useScenariosQuery(optimizationGroupId)

	return (
		<Popover
			onOpenChange={(open) => (open ? popover.open() : popover.close())}
			open={popover.isOpen}
		>
			<PopoverTrigger asChild>
				<Button onClick={popover.toggle}>
					{t('pc.action.add_to_scenario', {
						defaultValue: 'Add to what-if...',
					})}
					<ChevronDownIcon className='shrink-0 opacity-50' />
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' side='bottom' className='w-auto p-0'>
				<Command>
					<CommandInput
						placeholder={t('general_search')}
						autoFocus
						className='h-9'
					/>
					<CommandList>
						<CommandGroup>
							<CommandEmpty>
								{t('general_no_results_found', {
									defaultValue: 'No results found',
								})}
							</CommandEmpty>
							{scenariosQuery.data
								?.filter((scenario) => !scenario.is_main)
								?.map((scenario) => ({
									label: scenario.name,
									value: scenario.id,
								}))
								.map(({ value, label }) => (
									<CommandItem
										key={label}
										value={value}
										onSelect={() => {
											// TODO: 'what-if: add pcs to scenario' event not in Ampli tracking plan
											value &&
												dublicate.mutate({
													src_pricing_campaign_ids: selected,
													dst_entity_ids: [value],
													dst_entity_type: 'scenario',
												})
											popover.close()
										}}
									>
										{label}
									</CommandItem>
								))}
						</CommandGroup>
						<CommandSeparator />
						<CommandGroup>
							<CommandItem
								onSelect={() => {
									ampli.whatIfCreateScenarioModalOpen({
										location: 'pricing_campaigns_list',
									})
									showModal('CREATE_SCENARIO', {
										pricingCampaignsIds: selected,
										optimizationGroupId,
									})
									popover.close()
								}}
							>
								<PlusCircleIcon />
								{t('general_create')}
							</CommandItem>
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
